import React, { useEffect, useState, useMemo, useContext } from "react"
import { SessionContext } from "context/sessionContext"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Button,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
  Spinner,
} from "reactstrap"
import Select from "react-select"
import Swal from "sweetalert2"
import { sessionApi } from "apis/SessionApi"
import { studentMarkEntries } from "apis/MarkEntriesApi"
import { generateReports } from "apis/GradeCardApi"
import { GradeCardPreviewModal, generateGradeCardPdf, generateGradeCardFileForStudent } from "./Gradecardpdf"
import { classApi } from "apis/ClassListApi"

const selectStyles = {
  menu: p => ({ ...p, backgroundColor: "#fff", zIndex: 9999 }),
  menuList: p => ({ ...p, backgroundColor: "#fff", maxHeight: "200px" }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isFocused ? "#f8f9fa" : "#fff",
    color: "#000",
    cursor: "pointer",
  }),
}


/* ─────────────────────────────────────────────
   Main GradeCard component
 ───────────────────────────────────────────── */
const GradeCard = () => {
  document.title = "Grade Card"

  const { Session } = useContext(SessionContext)
  const activeSessionId = Session || sessionStorage.getItem("SessionId")

  // ── Class / Section ───────────────────────────────────────────────────
  const [classOptions, setClassOptions] = useState([])
  const [classLoading, setClassLoading] = useState(false)
  const [selectedClass, setSelectedClass] = useState(null)

  // ── Exams ─────────────────────────────────────────────────────────────
  const [examOptions, setExamOptions] = useState([])
  const [examsLoading, setExamsLoading] = useState(false)
  const [selectedExams, setSelectedExams] = useState([])

  // ── Students ──────────────────────────────────────────────────────────
  const [students, setStudents] = useState([])
  const [hasGenerated, setHasGenerated] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)

  // ── Search ────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("")
  const [appliedSearch, setAppliedSearch] = useState("")

  // ── Selection ─────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState([])
  const [isAllStudents, setIsAllStudents] = useState(false)

  // ── Pagination ────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  // ── Publish ─────────────────────────────────────────────────────────
  const [publishedIds, setPublishedIds] = useState([])
  const [isPublishing, setIsPublishing] = useState(false)
  const [isPublishAll, setIsPublishAll] = useState(false) // true = publish/unpublish ALL students across pages

  // ── Report preview ────────────────────────────────────────────────────
  const [previewOpen, setPreviewOpen] = useState(false)
  const [reports, setReports] = useState([])
  const [isLoadingReports, setIsLoadingReports] = useState(false)

  /* ── Fetch all classes from getAllClass API ── */
  useEffect(() => {
    ; (async () => {
      setClassLoading(true)
      try {
        const res = await classApi.getAllClass()
        const sections = res?.data?.section_list || []
        const options = sections.map(s => ({
          value: s.UID,
          label: `${s.st_class.toUpperCase()} ${s.st_sec.toUpperCase()}`,
          st_class: s.st_class,
          st_sec: s.st_sec,
        }))
        setClassOptions(options)
      } catch (err) {
        console.error("Failed to fetch classes:", err.message)
        Swal.fire("Error", "Failed to fetch class list.", "error")
      } finally {
        setClassLoading(false)
      }
    })()
  }, [])

  /* ── Cascade reset helpers ── */
  const resetStudentState = () => {
    setStudents([])
    setSelectedIds([])
    setPublishedIds([])
    setPublishSelectedIds([])
    setAllStudentCodesLoaded(false)
    setUserHasModifiedPublish(false)
    setHeaderPublishAction("")
    setIsPublishAll(false)
    setIsAllStudents(false)
    setHasGenerated(false)
    setSearchQuery("")
    setAppliedSearch("")
    setCurrentPage(1)
    setTotalPages(1)
    setTotalCount(0)
  }

  // Reset student states and selection when session changes
  useEffect(() => {
    resetStudentState()
    setSelectedClass(null)
  }, [activeSessionId])

  /* ── Class change ── */
  const handleClassChange = option => {
    setSelectedClass(option)
    setSelectedExams([])
    setExamOptions([])
    resetStudentState()
  }

  /* ── Fetch exams when class changes ── */
  useEffect(() => {
    if (!selectedClass) {
      setExamOptions([])
      setSelectedExams([])
      return
    }
    ; (async () => {
      setExamsLoading(true)
      try {
        const res = await generateReports.getAllExams({
          class: selectedClass.st_class,
          session_id: activeSessionId ? parseInt(activeSessionId, 10) : null,
        })
        if (res?.data?.status === 200 && Array.isArray(res.data.exams)) {
          const options = res.data.exams.map(e => ({
            value: e.id,
            label: e.exam_name,
          }))
          setExamOptions(options)
          setSelectedExams(options)
        } else {
          setExamOptions([])
          setSelectedExams([])
          console.warn("Failed to fetch exams:", res?.data?.msg)
        }
      } catch (err) {
        console.error("Failed to fetch exams:", err.message)
        setExamOptions([])
      } finally {
        setExamsLoading(false)
      }
    })()
  }, [selectedClass])

  /* ── Fetch students helper ── */
  const fetchStudents = async (page = 1, search = "") => {
    if (!selectedClass) return

    setIsLoading(true)
    try {
      const res = await studentMarkEntries.getMarkEntries({
        session_id: parseInt(activeSessionId, 10),
        st_class: selectedClass.st_class,
        st_sec: selectedClass.st_sec,
        page,
        search,
        exam_id: selectedExams.map(e => e.value),
      })

      if (res?.data?.status === 200 && Array.isArray(res.data.entries)) {
        const seen = new Set()
        const unique = []
        const published = []
        res.data.entries.forEach(e => {
          if (!seen.has(e.st_code)) {
            seen.add(e.st_code)
            unique.push({ st_code: e.st_code, st_name: e.st_name })
            if (e.is_published) {
              published.push(e.st_code)
            }
          }
        })
        setStudents(unique)
        setPublishedIds(prev => {
          // Merge published status from this page with existing
          const next = [...prev.filter(id => !unique.some(s => s.st_code === id))]
          return [...next, ...published]
        })
        setTotalPages(res.data.total_pages ?? 1)
        setTotalCount(res.data.total_count ?? res.data.entries.length)
        setCurrentPage(res.data.page_number ?? page)
        setHasGenerated(true)
      } else {
        Swal.fire(
          "Error",
          res?.data?.msg || "Failed to fetch student data.",
          "error"
        )
      }
    } catch (err) {
      console.error("Failed to fetch students:", err.message)
      Swal.fire(
        "Error",
        "Failed to fetch student data. Please try again.",
        "error"
      )
    } finally {
      setIsLoading(false)
    }
  }

  /* ── Load students ── */
  const handleLoadStudents = async () => {
    if (!selectedClass)
      return Swal.fire(
        "Validation",
        "Please select a Class / Section.",
        "warning"
      )

    if (selectedExams.length === 0)
      return Swal.fire(
        "Validation",
        "Please select at least one Exam.",
        "warning"
      )

    resetStudentState()
    setIsGenerating(true)
    await fetchStudents(1, "")
    setIsGenerating(false)

    // After loading, fetch all student codes across all pages and auto-save their grade cards
    ;(async () => {
      try {
        const allStCodes = []
        let page = 1
        let totalPagesAll = 1

        do {
          const res = await studentMarkEntries.getMarkEntries({
            session_id: parseInt(activeSessionId, 10),
            st_class: selectedClass.st_class,
            st_sec: selectedClass.st_sec,
            page,
            search: "",
            exam_id: selectedExams.map(e => e.value),
          })

          if (res?.data?.status === 200 && Array.isArray(res.data.entries)) {
            res.data.entries.forEach(e => {
              if (!allStCodes.includes(e.st_code)) {
                allStCodes.push(e.st_code)
              }
            })
            totalPagesAll = res.data.total_pages ?? 1
          } else {
            break
          }
          page++
        } while (page <= totalPagesAll)

        // Auto-save grade cards for all students
        if (allStCodes.length > 0) {
          autoSavePublishedGradeCards(allStCodes)
        }
      } catch (err) {
        console.error("Background fetch for students failed:", err.message)
      }
    })()
  }

  useEffect(() => {
    if (!hasGenerated) return
    if (searchQuery === appliedSearch) return

    const delayDebounceFn = setTimeout(() => {
      setAppliedSearch(searchQuery)
      fetchStudents(1, searchQuery)
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [searchQuery, hasGenerated, appliedSearch])

  /* ── Search ── */
  const handleSearch = () => {
    if (hasGenerated) {
      setAppliedSearch(searchQuery)
      fetchStudents(1, searchQuery)
    }
  }
  const handleSearchKeyDown = e => {
    if (e.key === "Enter") handleSearch()
  }

  const handlePageChange = page => {
    if (page < 1 || page > totalPages) return
    fetchStudents(page, appliedSearch)
  }

  const filteredData = students

  /* ── Checkbox helpers ── */
  const allChecked =
    filteredData.length > 0 &&
    filteredData.every(s => selectedIds.includes(s.st_code))
  const isIndeterminate =
    filteredData.some(s => selectedIds.includes(s.st_code)) && !allChecked

  const handleSelectAll = e => {
    if (e.target.checked) {
      setSelectedIds(prev => {
        const next = [...prev]
        filteredData.forEach(s => {
          if (!next.includes(s.st_code)) {
            next.push(s.st_code)
          }
        })
        return next
      })
    } else {
      setSelectedIds(prev =>
        prev.filter(id => !filteredData.some(s => s.st_code === id))
      )
    }
  }
  const handleSelectOne = (e, id) => {
    setSelectedIds(prev =>
      e.target.checked ? [...prev, id] : prev.filter(sid => sid !== id)
    )
  }

  /* ── Generate grade cards ── */
  const handleOpenGradeCard = async () => {
    if (!isAllStudents && selectedIds.length === 0)
      return Swal.fire(
        "Validation",
        "Please select at least one student.",
        "warning"
      )

    if (selectedExams.length === 0)
      return Swal.fire(
        "Validation",
        "Please select at least one Exam.",
        "warning"
      )

    setPreviewOpen(true)
    setIsLoadingReports(true)
    setReports([])

    try {
      const payload = {
        session_id: parseInt(activeSessionId, 10),
        exam_ids: selectedExams.map(e => e.value),
      }

      if (isAllStudents) {
        payload.is_all_students = true
        payload.st_class = selectedClass.st_class
        payload.st_sec = selectedClass.st_sec
      } else {
        payload.st_codes = selectedIds
        payload.student_ids = selectedIds
      }

      const res = await generateReports.getReportGenerated(payload)

      const data = res?.data ?? res

      if (
        data?.status === 200 &&
        Array.isArray(data.reports) &&
        data.reports.length > 0
      ) {
        const rootAverages = data.subject_averages || []
        const reportsWithAverages = data.reports.map(r => {
          const apiAverages = rootAverages.length > 0 ? rootAverages : (r.subject_averages || [])
          
          const exam1Name = r.exams?.[0]?.exam_name
          const exam2Name = r.exams?.[1]?.exam_name
          
          const cleanExam1 = exam1Name ? exam1Name.toLowerCase().replace(/[^a-z0-9]/g, "") : null
          const cleanExam2 = exam2Name ? exam2Name.toLowerCase().replace(/[^a-z0-9]/g, "") : null

          const subject_avg = apiAverages.map(item => {
            let avgVal = null
            let term1Val = null
            let term2Val = null

            Object.keys(item).forEach(key => {
              if (key !== "subject_code" && key !== "subject_name") {
                const val = item[key]
                const cleanKey = key.toLowerCase().replace(/[^a-z0-9]/g, "")
                
                // Match Term 1
                if (cleanKey.includes("term1") || cleanKey.includes("t1")) {
                  term1Val = val
                } else if (cleanExam1 && (cleanKey.includes(cleanExam1) || cleanExam1.includes(cleanKey))) {
                  term1Val = val
                }
                
                // Match Term 2
                if (cleanKey.includes("term2") || cleanKey.includes("t2")) {
                  term2Val = val
                } else if (cleanExam2 && (cleanKey.includes(cleanExam2) || cleanExam2.includes(cleanKey))) {
                  term2Val = val
                }

                if (val !== null && val !== undefined && val !== "") {
                  avgVal = val
                }
              }
            })
            return {
              subject_name: item.subject_name,
              subject_code: item.subject_code,
              term1_avg: term1Val,
              term2_avg: term2Val,
              average: avgVal,
            }
          })
          return {
            ...r,
            subject_avg,
            group: data.group || "9",
          }
        })

        setReports(reportsWithAverages)
        if (data.errors?.length > 0) {
          console.warn("Report generation errors:", data.errors)
        }

        // Auto-save grade card files to backend
        saveGradeCardFiles(reportsWithAverages)
      } else {
        const errMsg = data?.msg || "No report data returned."
        Swal.fire("Error", errMsg, "error")
        setPreviewOpen(false)
      }
    } catch (err) {
      console.error("Failed to generate reports:", err.message)
      Swal.fire(
        "Error",
        err.response?.data?.msg ||
        "Failed to generate grade cards. Please try again.",
        "error"
      )
      setPreviewOpen(false)
    } finally {
      setIsLoadingReports(false)
    }
  }

  /* ── Download PDF ── */
  const handleDownloadPdf = async () => {
    if (!reports || reports.length === 0) return
    await generateGradeCardPdf(reports)
  }

  /* ── Save Grade Card Files to Backend ── */
  const saveGradeCardFiles = async (reportsData) => {
    if (!reportsData || reportsData.length === 0) return

    try {
      // Small delay to ensure the modal DOM (with logo images) is rendered
      await new Promise(resolve => setTimeout(resolve, 500))

      const formData = new FormData()
      formData.append("session_id", parseInt(activeSessionId, 10))

      // Append exam_ids
      selectedExams.forEach(exam => {
        formData.append("exam_ids", exam.value)
      })

      // Build student_files JSON list and append each grade card file
      const studentFilesList = []

      for (let i = 0; i < reportsData.length; i++) {
        const report = reportsData[i]
        const stCode = report.student_info?.st_code
        if (!stCode) continue

        const pdfFile = await generateGradeCardFileForStudent(report)
        if (!pdfFile) continue

        const fileFieldName = `file_${i}`
        formData.append(fileFieldName, pdfFile)
        studentFilesList.push({
          st_code: stCode,
          file_field: fileFieldName,
        })
      }

      formData.append("student_files", JSON.stringify(studentFilesList))

      await generateReports.saveGradeCardFile(formData)
      console.log("Grade card files saved successfully.")
    } catch (err) {
      console.error("Failed to save grade card files:", err.message)
    }
  }

  /* ── Auto-save grade cards for published students in background ── */
  const autoSavePublishedGradeCards = async (publishedStCodes) => {
    if (!publishedStCodes || publishedStCodes.length === 0) return
    if (!selectedClass || !activeSessionId || selectedExams.length === 0) return

    try {
      console.log(`Auto-saving grade cards for ${publishedStCodes.length} published students...`)

      // Call Generate_ReportCard API for published students
      const payload = {
        session_id: parseInt(activeSessionId, 10),
        exam_ids: selectedExams.map(e => e.value),
        st_codes: publishedStCodes,
        student_ids: publishedStCodes,
      }

      const res = await generateReports.getReportGenerated(payload)
      const data = res?.data ?? res

      if (
        data?.status === 200 &&
        Array.isArray(data.reports) &&
        data.reports.length > 0
      ) {
        // Transform report data (same logic as handleOpenGradeCard)
        const rootAverages = data.subject_averages || []
        const reportsWithAverages = data.reports.map(r => {
          const apiAverages = rootAverages.length > 0 ? rootAverages : (r.subject_averages || [])

          const exam1Name = r.exams?.[0]?.exam_name
          const exam2Name = r.exams?.[1]?.exam_name

          const cleanExam1 = exam1Name ? exam1Name.toLowerCase().replace(/[^a-z0-9]/g, "") : null
          const cleanExam2 = exam2Name ? exam2Name.toLowerCase().replace(/[^a-z0-9]/g, "") : null

          const subject_avg = apiAverages.map(item => {
            let avgVal = null
            let term1Val = null
            let term2Val = null

            Object.keys(item).forEach(key => {
              if (key !== "subject_code" && key !== "subject_name") {
                const val = item[key]
                const cleanKey = key.toLowerCase().replace(/[^a-z0-9]/g, "")

                if (cleanKey.includes("term1") || cleanKey.includes("t1")) {
                  term1Val = val
                } else if (cleanExam1 && (cleanKey.includes(cleanExam1) || cleanExam1.includes(cleanKey))) {
                  term1Val = val
                }

                if (cleanKey.includes("term2") || cleanKey.includes("t2")) {
                  term2Val = val
                } else if (cleanExam2 && (cleanKey.includes(cleanExam2) || cleanExam2.includes(cleanKey))) {
                  term2Val = val
                }

                if (val !== null && val !== undefined && val !== "") {
                  avgVal = val
                }
              }
            })
            return {
              subject_name: item.subject_name,
              subject_code: item.subject_code,
              term1_avg: term1Val,
              term2_avg: term2Val,
              average: avgVal,
            }
          })
          return {
            ...r,
            subject_avg,
            group: data.group || "9",
          }
        })

        // Wait a bit for hidden logo images to be fully loaded
        await new Promise(resolve => setTimeout(resolve, 800))

        // Save grade card files to backend
        await saveGradeCardFiles(reportsWithAverages)
        console.log("Auto-save of published grade cards completed.")
      } else {
        console.warn("Auto-save: No report data returned for published students.")
      }
    } catch (err) {
      console.error("Auto-save of published grade cards failed:", err.message)
    }
  }

  // ── Publish Selection (local toggle, no API call) ─────────────────────
  const [publishSelectedIds, setPublishSelectedIds] = useState([])
  const [allStudentCodesLoaded, setAllStudentCodesLoaded] = useState(false)
  const [userHasModifiedPublish, setUserHasModifiedPublish] = useState(false)
  const [headerPublishAction, setHeaderPublishAction] = useState("")

  // When students are loaded, sync publishSelectedIds with actual publishedIds from API
  // BUT only for students that the user hasn't already manually toggled
  useEffect(() => {
    if (students.length > 0 && !userHasModifiedPublish) {
      setPublishSelectedIds(prev => {
        const next = [...prev.filter(id => !students.some(s => s.st_code === id))]
        students.forEach(s => {
          if (publishedIds.includes(s.st_code)) {
            next.push(s.st_code)
          }
        })
        return next
      })
    }
  }, [students, publishedIds])

  const handlePublishSelectOne = (stCode, isCurrentlySelected) => {
    setUserHasModifiedPublish(true)
    if (isCurrentlySelected) {
      setPublishSelectedIds(prev => prev.filter(id => id !== stCode))
    } else {
      setPublishSelectedIds(prev => [...prev, stCode])
    }
  }

  const handlePublishSelectAll = () => {
    const allSelected = filteredData.length > 0 && filteredData.every(s => publishSelectedIds.includes(s.st_code))
    if (allSelected) {
      // Unselect ALL students across all pages
      setPublishSelectedIds([])
    } else {
      // Select all students on current page (others remain as-is)
      setPublishSelectedIds(prev => {
        const next = [...prev]
        filteredData.forEach(s => {
          if (!next.includes(s.st_code)) next.push(s.st_code)
        })
        return next
      })
    }
  }

  /* ── Header action: Publish All / Unpublish All across ALL pages ── */
  const handleHeaderPublishAction = async (action) => {
    if (!selectedClass || !activeSessionId) return
    setUserHasModifiedPublish(true)
    setHeaderPublishAction(action)

    if (action === "unpublish") {
      setPublishSelectedIds([])
      return
    }

    // Immediately set current page students to publish for instant UI feedback
    setPublishSelectedIds(prev => {
      const next = [...prev]
      filteredData.forEach(s => {
        if (!next.includes(s.st_code)) next.push(s.st_code)
      })
      return next
    })

    // For "publish" — fetch all student codes across all pages and select them all
    try {
      const allStCodes = []
      let page = 1
      let totalPagesAll = 1

      do {
        const res = await studentMarkEntries.getMarkEntries({
          session_id: parseInt(activeSessionId, 10),
          st_class: selectedClass.st_class,
          st_sec: selectedClass.st_sec,
          page,
          search: "",
          exam_id: selectedExams.map(e => e.value),
        })

        if (res?.data?.status === 200 && Array.isArray(res.data.entries)) {
          res.data.entries.forEach(e => {
            if (!allStCodes.includes(e.st_code)) {
              allStCodes.push(e.st_code)
            }
          })
          totalPagesAll = res.data.total_pages ?? 1
        } else {
          break
        }
        page++
      } while (page <= totalPagesAll)

      setPublishSelectedIds(allStCodes)
    } catch (err) {
      console.error("Failed to fetch all students for publish all:", err.message)
    }
  }

  /* ── Publish / Unpublish Grade Card (called from Publish button) ── */
  const handlePublishClick = async () => {
    if (selectedExams.length === 0) {
      return Swal.fire("Validation", "Please select at least one Exam.", "warning")
    }
    if (!selectedClass || !activeSessionId) return

    setIsPublishing(true)
    try {
      // Fetch ALL student codes and their current published status across all pages
      const allStCodes = []
      const apiPublishedCodes = []
      let page = 1
      let totalPagesAll = 1

      do {
        const res = await studentMarkEntries.getMarkEntries({
          session_id: parseInt(activeSessionId, 10),
          st_class: selectedClass.st_class,
          st_sec: selectedClass.st_sec,
          page,
          search: "",
          exam_id: selectedExams.map(e => e.value),
        })

        if (res?.data?.status === 200 && Array.isArray(res.data.entries)) {
          res.data.entries.forEach(e => {
            if (!allStCodes.includes(e.st_code)) {
              allStCodes.push(e.st_code)
              if (e.is_published) {
                apiPublishedCodes.push(e.st_code)
              }
            }
          })
          totalPagesAll = res.data.total_pages ?? 1
        } else {
          break
        }
        page++
      } while (page <= totalPagesAll)

      if (allStCodes.length === 0) {
        Swal.fire("Error", "No students found.", "error")
        setIsPublishing(false)
        return
      }

      setAllStudentCodesLoaded(true)

      // Only send changes for students whose status was actually modified by the user.
      // If user has modified publish selections, use publishSelectedIds as source of truth.
      // Otherwise, keep current API status.
      const toPublish = []
      const toUnpublish = []

      allStCodes.forEach(stCode => {
        const isInPublishSelected = publishSelectedIds.includes(stCode)
        const wasPublishedOnApi = apiPublishedCodes.includes(stCode)

        if (userHasModifiedPublish) {
          // User has interacted with publish selections — trust publishSelectedIds
          if (isInPublishSelected) {
            toPublish.push(stCode)
          } else {
            toUnpublish.push(stCode)
          }
        } else {
          // User never modified publish — keep current API status
          if (wasPublishedOnApi) {
            toPublish.push(stCode)
          } else {
            toUnpublish.push(stCode)
          }
        }
      })

      const promises = []

      if (toPublish.length > 0) {
        promises.push(
          generateReports.publishGradeCard({
            session_id: parseInt(activeSessionId, 10),
            exam_ids: selectedExams.map(e => e.value),
            st_codes: toPublish,
            action: "publish",
          })
        )
      }

      if (toUnpublish.length > 0) {
        promises.push(
          generateReports.publishGradeCard({
            session_id: parseInt(activeSessionId, 10),
            exam_ids: selectedExams.map(e => e.value),
            st_codes: toUnpublish,
            action: "unpublish",
          })
        )
      }

      const results = await Promise.all(promises)
      const allSuccess = results.every(r => r?.data?.status === 200)

      if (allSuccess) {
        setPublishedIds(toPublish)
        setPublishSelectedIds(toPublish)
        setUserHasModifiedPublish(false)
        Swal.fire("Success", `${toPublish.length} published, ${toUnpublish.length} unpublished.`, "success")
      } else {
        Swal.fire("Error", "Some operations failed. Please try again.", "error")
      }
    } catch (err) {
      console.error("Failed to publish/unpublish grade cards:", err.message)
      Swal.fire("Error", err.response?.data?.msg || "Failed to publish grade cards. Please try again.", "error")
    } finally {
      setIsPublishing(false)
    }
  }

  /* ── Reset ── */
  const handleReset = () => {
    setSelectedClass(null)
    setSelectedExams([])
    setExamOptions([])
    resetStudentState()
  }

  /* ── Table body ── */
  const renderTableBody = () => {
    if (!hasGenerated && !isLoading) {
      return (
        <tr>
          <td colSpan={4} className="text-center py-5 text-muted">
            <i
              className="bx bx-list-ul"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            Select a Class / Section and click <strong>Load Students</strong>.
          </td>
        </tr>
      )
    }
    if (isLoading) {
      return (
        <tr>
          <td colSpan={4} className="text-center py-5 text-muted">
            <i
              className="bx bx-loader-alt bx-spin"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            Loading students…
          </td>
        </tr>
      )
    }
    if (filteredData.length === 0) {
      return (
        <tr>
          <td colSpan={4} className="text-center py-5 text-muted">
            <i
              className="bx bx-search-alt"
              style={{
                fontSize: "2rem",
                display: "block",
                marginBottom: "8px",
              }}
            />
            {appliedSearch
              ? "No students match your search."
              : "No student records found."}
          </td>
        </tr>
      )
    }
    return filteredData.map(student => {
      const isPublishSelected = publishSelectedIds.includes(student.st_code)
      return (
        <tr key={student.st_code}>
          <td className="text-center">
            {!isAllStudents && (
              <Input
                type="checkbox"
                checked={selectedIds.includes(student.st_code)}
                onChange={e => handleSelectOne(e, student.st_code)}
              />
            )}
          </td>
          <td className="text-center">{student.st_code}</td>
          <td>{student.st_name}</td>
          <td className="text-center">
            <select
              value={isPublishSelected ? "publish" : "unpublish"}
              onChange={e => {
                const val = e.target.value
                if (val === "publish" && !isPublishSelected) {
                  handlePublishSelectOne(student.st_code, false)
                } else if (val === "unpublish" && isPublishSelected) {
                  handlePublishSelectOne(student.st_code, true)
                }
              }}
              style={{
                padding: "4px 8px",
                fontSize: "12px",
                fontWeight: 600,
                borderRadius: "4px",
                border: `1px solid ${isPublishSelected ? "#16a34a" : "#dc2626"}`,
                background: isPublishSelected ? "#dcfce7" : "#fee2e2",
                color: isPublishSelected ? "#166534" : "#991b1b",
                cursor: "pointer",
                outline: "none",
              }}
            >
              <option value="publish">Publish</option>
              <option value="unpublish">Unpublish</option>
            </select>
          </td>
        </tr>
      )
    })
  }

  // ── Pagination ───────────────────────────────────────────────────────────
  const renderPagination = () => {
    if (!hasGenerated || totalPages <= 1) return null
    const pages = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1)
    for (let i = start; i <= end; i++) pages.push(i)

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Showing page <strong>{currentPage}</strong> of{" "}
          <strong>{totalPages}</strong>
          &nbsp;|&nbsp; Total: <strong>{totalCount}</strong> students
        </small>
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(1)}>
              «
            </button>
          </li>
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage - 1)}
            >
              ‹
            </button>
          </li>
          {pages.map(p => (
            <li
              key={p}
              className={`page-item ${p === currentPage ? "active" : ""}`}
            >
              <button className="page-link" onClick={() => handlePageChange(p)}>
                {p}
              </button>
            </li>
          ))}
          <li
            className={`page-item ${currentPage === totalPages ? "disabled" : ""
              }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage + 1)}
            >
              ›
            </button>
          </li>
          <li
            className={`page-item ${currentPage === totalPages ? "disabled" : ""
              }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(totalPages)}
            >
              »
            </button>
          </li>
        </ul>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Container fluid={true}>
            <Breadcrumb title="Examination" breadcrumbItem="Marksheet" />

            {/* ── Filter Section ── */}
            <div className="p-3 mb-4 rounded bg-white">
              <Row>
                {/* Class / Section */}
                <Col lg={3} md={6} className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Class / Section <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="st_class"
                    classNamePrefix="select2-selection"
                    placeholder={
                      classLoading
                        ? "Loading classes…"
                        : classOptions.length === 0
                          ? "No classes available"
                          : "Select Class / Section..."
                    }
                    options={classOptions}
                    value={selectedClass}
                    onChange={handleClassChange}
                    isLoading={classLoading}
                    isDisabled={classLoading || classOptions.length === 0}
                    styles={selectStyles}
                  />
                </Col>

                {/* Exam */}
                <Col lg={5} md={6} className="mb-3">
                  <label className="form-label fw-semibold mb-1">
                    Exam <span className="text-danger">*</span>
                  </label>
                  <Select
                    name="exams"
                    isMulti
                    classNamePrefix="select2-selection"
                    placeholder={
                      examsLoading
                        ? "Loading exams…"
                        : !selectedClass
                          ? "Select Class / Section first"
                          : examOptions.length === 0
                            ? "No exams available"
                            : "Select Exams..."
                    }
                    options={examOptions}
                    value={selectedExams}
                    onChange={setSelectedExams}
                    isLoading={examsLoading}
                    isDisabled={examsLoading || !selectedClass || examOptions.length === 0}
                    styles={selectStyles}
                  />
                </Col>
              </Row>

              <Row className="mt-1">
                <Col>
                  <Button
                    color="primary"
                    className="me-2"
                    onClick={handleLoadStudents}
                    disabled={isLoading || isGenerating || !selectedClass || !selectedExams || selectedExams.length === 0}
                  >
                    <i className="bx bx-list-ul me-1" />
                    {isGenerating ? "Loading…" : "Load Students"}
                  </Button>
                  <Button
                    color="secondary"
                    outline
                    onClick={handleReset}
                    disabled={isLoading}
                  >
                    <i className="bx bx-reset me-1" />
                    Reset
                  </Button>
                </Col>
              </Row>
            </div>

            {/* ── Search + Generate ── */}
            <Row className="mb-3 align-items-center">
              <Col lg={5} md={12} className="mb-2 mb-lg-0">
                <div className="d-flex gap-2">
                  <div style={{ position: "relative", width: "100%" }}>
                    <Input
                      type="text"
                      placeholder="Search by Student Name or Student Code"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      disabled={!hasGenerated}
                      style={{ fontSize: "13px", paddingRight: "30px" }}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery("")
                          setAppliedSearch("")
                          fetchStudents(1, "")
                        }}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                          fontSize: "12px",
                          padding: 0,
                          color: "#6c757d",
                          zIndex: 5,
                        }}
                      >
                        ❌
                      </button>
                    )}
                  </div>
                  <Button
                    color="primary"
                    onClick={handleSearch}
                    disabled={!hasGenerated || isLoading}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <i className="bx bx-search me-1" />
                    Search
                  </Button>
                </div>
              </Col>

              <Col lg={7} md={12}>
                <div className="d-flex align-items-center justify-content-lg-end gap-2 flex-wrap">
                  {isAllStudents ? (
                    <span
                      className="badge rounded-pill text-white"
                      style={{
                        background: "#6f42c1",
                        fontSize: "13px",
                        padding: "6px 14px",
                        fontWeight: 600,
                      }}
                    >
                      <i className="bx bx-check-circle me-1" />
                      Selected: {totalCount} students
                    </span>
                  ) : selectedIds.length > 0 ? (
                    <span
                      className="badge rounded-pill"
                      style={{
                        background: "#ede9fe",
                        color: "#5b21b6",
                        fontSize: "13px",
                        padding: "6px 14px",
                        fontWeight: 600,
                      }}
                    >
                      <i className="bx bx-check-circle me-1" />
                      Selected: {selectedIds.length}{" "}
                      {selectedIds.length === 1 ? "Student" : "Students"}
                    </span>
                  ) : null}
                  <Button
                    color="success"
                    size="md"
                    onClick={handleOpenGradeCard}
                    disabled={!isAllStudents && selectedIds.length === 0}
                  >
                    <i className="bx bx-id-card me-1" />
                    Generate Marksheet
                  </Button>
                  <Button
                    color="primary"
                    size="md"
                    onClick={handlePublishClick}
                    disabled={!hasGenerated || isPublishing || selectedExams.length === 0}
                  >
                    {isPublishing ? (
                      <>
                        <i className="bx bx-loader-alt bx-spin me-1" />
                        Publishing…
                      </>
                    ) : (
                      <>
                        <i className="bx bx-upload me-1" />
                        Publish
                      </>
                    )}
                  </Button>
                </div>
              </Col>
            </Row>

            {/* ── Small information banner below search bar ── */}
            {allChecked && totalCount > filteredData.length && !isAllStudents && (
              <div className="alert alert-info d-flex align-items-center justify-content-between p-2 mb-3" style={{ fontSize: "13px" }}>
                <span>{selectedIds.length} students selected. <Button
                  color="link"
                  className="p-0 ms-2 font-weight-medium text-decoration-underline text-info"
                  onClick={() => setIsAllStudents(true)}
                  style={{ border: "none", background: "none", verticalAlign: "baseline" }}
                >
                  Select all {totalCount} Students
                </Button></span>

              </div>
            )}
            {isAllStudents && (
              <div className="alert alert-info d-flex align-items-center justify-content-between p-2 mb-3" style={{ fontSize: "13px" }}>
                <span>All {totalCount} students selected for Grade Card generation.</span>
                <Button
                  color="link"
                  className="p-0 ms-2 font-weight-medium text-decoration-underline text-info"
                  onClick={() => {
                    setIsAllStudents(false)
                    setSelectedIds([])
                  }}
                  style={{ border: "none", background: "none", verticalAlign: "baseline" }}
                >
                  Clear selection
                </Button>
              </div>
            )}

            {/* ── Publish status banner ── */}
            {isPublishing && (
              <div className="alert alert-warning d-flex align-items-center p-2 mb-3" style={{ fontSize: "13px" }}>
                <i className="bx bx-loader-alt bx-spin me-2" style={{ fontSize: "1.1rem" }} />
                <span style={{ fontWeight: 500 }}>
                  Publishing grade cards… Please wait.
                </span>
              </div>
            )}

            {/* ── Table ── */}
            <div className="table-responsive">
              <Table className="table table-striped table-bordered">
                <thead>
                  <tr className="text-center table-secondary">
                    <th style={{ width: "48px" }}>
                      {!isAllStudents && (
                        <Input
                          type="checkbox"
                          checked={allChecked}
                          innerRef={el => {
                            if (el) el.indeterminate = isIndeterminate
                          }}
                          onChange={handleSelectAll}
                          disabled={!hasGenerated || filteredData.length === 0}
                        />
                      )}
                    </th>
                    <th className="text-center" style={{ width: "140px" }}>
                      Student Code
                    </th>
                    <th className="text-start">Student Name</th>
                    <th className="text-center" style={{ width: "160px" }}>
                      <select
                        value={headerPublishAction}
                        onChange={e => {
                          const val = e.target.value
                          if (val) {
                            handleHeaderPublishAction(val)
                          }
                        }}
                        disabled={!hasGenerated || filteredData.length === 0}
                        style={{
                          padding: "5px 10px",
                          fontSize: "12px",
                          fontWeight: 700,
                          borderRadius: "6px",
                          border: `2px solid ${headerPublishAction === "publish" ? "#16a34a" : headerPublishAction === "unpublish" ? "#dc2626" : "#6366f1"}`,
                          background: headerPublishAction === "publish" ? "#dcfce7" : headerPublishAction === "unpublish" ? "#fee2e2" : "#eef2ff",
                          color: headerPublishAction === "publish" ? "#166534" : headerPublishAction === "unpublish" ? "#991b1b" : "#4338ca",
                          cursor: "pointer",
                          outline: "none",
                          appearance: "auto",
                        }}
                      >
                        <option value="" disabled>Publish / Unpublish</option>
                        <option value="publish">Publish All</option>
                        <option value="unpublish">Unpublish All</option>
                      </select>
                    </th>
                  </tr>
                </thead>
                <tbody>{renderTableBody()}</tbody>
              </Table>
            </div>
            {renderPagination()}
          </Container>
        </div>
      </div>
      <GradeCardPreviewModal
        isOpen={previewOpen}
        toggle={() => setPreviewOpen(false)}
        reports={reports}
        isLoadingReports={isLoadingReports}
        onDownloadPdf={handleDownloadPdf}
      />
      {/* Hidden logos always in DOM for background PDF generation */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px", opacity: 0, pointerEvents: "none" }}>
        <img id="preview-cbse-logo" src="/images/assets/cbse_logo.png" alt="CBSE" crossOrigin="anonymous" />
        <img id="preview-school-logo" src="/images/assets/sps_logo.png" alt="School" crossOrigin="anonymous" />
      </div>
    </React.Fragment>
  )
}

export default GradeCard
